// useReportData.js
// Generic report data-fetching hook: builds a query string from the shared
// filter shape ({startDate, endDate, departmentId}) and fetches from
// whichever report endpoint the caller names. Each report view supplies
// its own endpoint path and consumes data's shape however it needs to —
// this hook only owns fetch/loading/error and the matching CSV export URL.
//
// extraParams carries report-specific filter dimensions the shared filter
// shape doesn't know about (e.g. a trainer_id filter only one report uses)
// — kept as a plain caller-supplied object rather than hardcoding every
// possible dimension into this hook.

import { useState, useEffect } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function useReportData(endpointPath, filters, extraParams = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // filters being present at all (the filter bar has fired at least once) is
  // what makes a report "ready" to fetch — startDate/endDate may legitimately
  // be absent, e.g. an "All Time" period with no date range to send.
  const params = filters
    ? new URLSearchParams({
        ...(filters.startDate && filters.endDate ? { start: filters.startDate, end: filters.endDate } : {}),
        ...(filters.departmentId ? { department_id: filters.departmentId } : {}),
        ...Object.fromEntries(Object.entries(extraParams).filter(([, v]) => v !== undefined && v !== null && v !== '')),
      })
    : null;
  const queryString = params ? params.toString() : null;
  const ready = filters != null;

  useEffect(() => {
    if (!ready) return;
    setLoading(true);
    setError(null);
    const suffix = queryString ? `?${queryString}` : '';
    fetch(`${API_BASE_URL}/${endpointPath}${suffix}`, { credentials: 'include' })
      .then(response => response.json())
      .then(json => {
        if (json.error) {
          setError(json.error);
          setData(null);
        } else {
          setData(json);
        }
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [endpointPath, queryString, ready]);

  const exportUrl = ready
    ? `${API_BASE_URL}/${endpointPath}${queryString ? `?${queryString}&format=csv` : '?format=csv'}`
    : null;

  return { data, loading, error, exportUrl };
}

export default useReportData;
